<template>
  <v-container id="order-workflows-table" fluid>
    <v-card outlined :disabled="loadingData">
      <v-data-table
        :headers="computedHeaders"
        :items="orderWorkflows"
        :options.sync="options"
        :server-items-length="totalOrderWorkflows"
        :footer-props="footerProps"
        :loading="loadingData"
        :loading-text="$t('orderWorkflow.workflowsLoading')"
        :no-data-text="$t('orderWorkflow.noWorkflow')"
        mobile-breakpoint="650"
        item-key="id"
        dense
        :header-props="headerProps"
      >
        <template v-slot:top>
          <v-toolbar flat>
            <v-btn color="primary" outlined @click="createNewWorkflow"><v-icon>mdi-plus</v-icon></v-btn>
            <v-spacer></v-spacer>
            <v-btn color="primary" outlined @click="reloadList()">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-toolbar>
        </template>

        <template v-slot:item.copy="{ item }">
          <v-row justify-sm="end">
            <v-icon @click="copyReference(item._id)">mdi-content-copy</v-icon>
          </v-row>
        </template>

        <template v-slot:item._id="{ item }">
          {{ item._id }}
        </template>

        <template v-slot:item.action="{ item }">
          <v-icon v-if="isMobile" @click="copyReference(item._id)">mdi-content-copy</v-icon>
          <v-btn :class="[!isMobile ? '' : 'pr-0']" color="primary" text @click="executeAction(item._id)">
            <v-icon v-if="$vuetify.breakpoint.width >= 650 && $vuetify.breakpoint.width <= 768">mdi-magnify</v-icon>
            <span v-else>{{ $t('orderWorkflow.viewDetails') }}</span>
          </v-btn>
        </template>

        <template v-slot:item.activated="{ item }">
          <v-simple-checkbox v-model="item.activated" disabled></v-simple-checkbox>
        </template>

        <template v-slot:item.name="{ item }">
          <span>{{ item.name }}</span>
        </template>
      </v-data-table>
      <v-divider></v-divider>
      <v-card-actions class="justify-center">
        <v-btn outlined tile :href="safeWorkflowSimulatorURL" target="_blank" rel="noopener noreferrer">
          {{ $t('orderWorkflow.workflowSimulator') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script>
  import { mapState, mapMutations } from 'vuex'
  import { EventBus } from '../../event-bus'
  import { sanitizeUrl } from '@braintree/sanitize-url'

  export default {
    name: 'OrderWorkflowsTable',
    data() {
      return {
        orderWorkflows: [],
        totalOrderWorkflows: 0,
        options: {
          itemsPerPage: 15,
          sortDesc: [],
        },
        loadingData: false,
        componentPreviousPath: '',
        headersMobile: [
          {
            text: this.$t('orderWorkflow.reference'),
            value: '_id',
            filterType: 'text',
          },
          {
            text: this.$t('orderWorkflow.name'),
            value: 'name',
            filterType: 'text',
          },
          {
            text: this.$t('orderWorkflow.active'),
            value: 'activated',
            filterType: 'text',
          },
          {
            text: '',
            value: 'action',
            sortable: false,
          },
        ],
        headers: [
          {
            text: '',
            value: 'copy',
            sortable: false,
          },
          {
            text: this.$t('orderWorkflow.reference'),
            value: '_id',
            filterType: 'text',
          },
          {
            text: this.$t('orderWorkflow.name'),
            value: 'name',
            filterType: 'text',
          },
          {
            text: this.$t('orderWorkflow.active'),
            value: 'activated',
            filterType: 'text',
          },
          {
            text: '',
            value: 'action',
            sortable: false,
          },
        ],
        workflowSimulatorURL: process.env.VUE_APP_WORKFLOW_SIMULATOR_URL,
      }
    },
    watch: {
      options: {
        handler() {
          this.list()
        },
      },
      $props: {
        // any change on props reload datas, use separate watcher for each prop did not work (double event)
        handler() {
          if (this.$store.state.previousPath.split('/')[1] === 'orders') {
            let translatedStatus = []
            for (let sts of this.status) {
              translatedStatus.push(
                this.statusTranslations.find((statusTranslation) => statusTranslation.value === sts)
              )
            }
            this.formFilter.find((header) => header.value === 'current_status').arrayValues = translatedStatus

            let translatedTypes = []
            for (let type of this.types) {
              translatedTypes.push(this.typeTranslations.find((typeTranslation) => typeTranslation.value === type))
            }
            this.formFilter.find((header) => header.value === 'type').arrayValues = translatedTypes

            this.orderWorkflows = []
            this.list()
          }
        },
        deep: true,
        immediate: true,
      },
    },
    computed: {
      safeWorkflowSimulatorURL() {
        return sanitizeUrl(this.workflowSimulatorURL)
      },
      isMobile() {
        return this.$vuetify.breakpoint.width < 650
      },
      ...mapState(['loading']),
      footerProps() {
        return {
          'items-per-page-options': [15, 30, 50],
          'items-per-page-text': this.$t('orderWorkflow.workflowsPerPage'),
        }
      },
      headerProps() {
        return {
          sortByText: this.$t('orderWorkflow.sortByText'),
        }
      },
      computedHeaders() {
        if (this.isMobile) {
          return this.headersMobile
        } else {
          return this.headers
        }
      },
      alreadyAffectedDeliveryTypes() {
        return this.orderWorkflows
          .filter((workflow) => workflow.delivery_type !== undefined)
          .map((workflow) => workflow.delivery_type)
      },
    },
    methods: {
      ...mapMutations({
        setLoading: 'SET_LOADING',
      }),
      executeAction: function (workflowId) {
        this.$router.push({
          name: 'OrderWorkflowsDetails',
          params: { id: workflowId, alreadyAffectedDeliveryTypes: this.alreadyAffectedDeliveryTypes },
        })
      },
      createNewWorkflow: function () {
        this.$router.push({
          name: 'OrderWorkflowsAdd',
          params: { alreadyAffectedDeliveryTypes: this.alreadyAffectedDeliveryTypes },
        })
      },
      list() {
        this.loadingData = true
        let { page, itemsPerPage, sortDesc, sortBy } = this.options
        let myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
          queryStringParameters: {
            page: page,
            itemsPerPage: itemsPerPage,
            sortBy: sortBy.length === 0 ? 'date' : sortBy,
            sortDesc: sortDesc.length === 0 ? true : sortDesc,
          },
        }
        this.$Amplify.API.get('order-workflows', '/v1/int/order-workflows', myInit)
          .then((res) => {
            this.orderWorkflows = res.data
            this.totalOrderWorkflows = res.nbOrderWorkflows
            this.loadingData = false
            if (this.totalOrderWorkflows < (this.options.page - 1) * this.options.itemsPerPage + 1) {
              let pageNumber = Math.floor(this.totalOrderWorkflows / this.options.itemsPerPage)
              this.options.page = pageNumber === 0 ? 1 : pageNumber
            }
          })
          .catch((e) => {
            this.loadingData = false
            console.log(e)
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('orderWorkflow.errorLoadingWorkflows'),
              color: 'error',
            })
          })
      },
      async reloadList() {
        await this.$auth.isAuthenticated()
        EventBus.$emit('reLoadCounter')
        this.list()
      },
      copyReference: function (itemId) {
        navigator.clipboard.writeText(itemId).then(
          () => {
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('orderWorkflow.copySuccessful'),
              color: 'success',
            })
          },
          () => {
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('orderWorkflow.copyFailed'),
              color: 'error',
            })
          }
        )
      },
    },
    // we save the current path, to have it when this component will be reused later
    beforeRouteLeave(to, from, next) {
      this.componentPreviousPath = from.path
      next()
    },
  }
</script>
<style scoped>
  .w-100 {
    width: 100%;
  }
</style>
